// src/pages/services/MobileAppPage.jsx
import { motion } from "framer-motion";
import Lottie from "lottie-react";
import { FaAndroid, FaApple } from "react-icons/fa";
import mobileAnim from "../../assets/lottie/mobile1.json";

export default function MobileAppPage() {
  return (
    <div className="w-full bg-transparent text-black pt-12 px-6 md:px-20 pb-24">

      {/* ================= HERO ================= */}
      <section className="mx-auto flex max-w-7xl flex-col items-center gap-10 text-center">

        <motion.div
          initial={{ opacity: 0, y: 25 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="hero-heading mb-6">
            Mobile App Development
          </h1>

          <p className="hero-copy">
            We design and build production ready mobile apps that are fast,
            scalable, and built for real users, not demos.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex justify-center"
        >
          <Lottie animationData={mobileAnim} loop style={{ width: "85%", maxWidth: 420 }} />
        </motion.div>

      </section>

      {/* ================= WHAT WE DELIVER ================= */}
      <section className="mx-auto mt-28 max-w-6xl text-center">
        <h2 className="section-heading mb-10">
          What We Deliver
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
          {[
            { text: "Native Android apps", icon: <FaAndroid /> },
            { text: "Native iOS apps", icon: <FaApple /> },
            { text: "Cross platform solutions", icon: "⚡" },
            { text: "Clean & scalable architecture", icon: "📐" },
            { text: "Secure API & backend integration", icon: "🔐" },
            { text: "Production ready builds", icon: "🚀" },
          ].map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              className="glass-card rounded-[22px] p-6 flex items-center gap-4"
            >
              <div className="text-xl">{item.icon}</div>
              <h3 className="text-[16px] font-medium">{item.text}</h3>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ================= PROCESS ================= */}
      <section className="mx-auto mt-32 max-w-6xl text-center">
        <h2 className="section-heading mb-10">
          Our Process
        </h2>

        <div className="space-y-14">
          {[
            ["01", "Product Discovery", "Understanding the idea, users, and success metrics."],
            ["02", "UX Planning", "Defining flows, screens, and core interactions."],
            ["03", "UI Design", "Creating a polished and consistent visual system."],
            ["04", "Development", "Writing clean, scalable, and testable code."],
            ["05", "Testing & QA", "Ensuring performance, stability, and reliability."],
            ["06", "Launch & Support", "App store deployment and support after launch."],
          ].map(([step, title, desc], i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              className="glass-card flex gap-6 items-start rounded-[22px] p-5"
            >
              <h3 className="text-4xl font-semibold text-black">{step}</h3>
              <div>
                <h4 className="text-xl font-semibold mb-1">{title}</h4>
                <p className="text-black/60 text-[15px] leading-[1.6]">{desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ================= WHAT WE NEED ================= */}
      <section className="mx-auto mt-32 max-w-6xl text-center">
        <h2 className="section-heading mb-6">
          What We Need From You
        </h2>

        <ul className="mx-auto max-w-3xl space-y-4 text-center text-[16px] text-neutral-700">
          <li>• Clear business idea or problem statement</li>
          <li>• Target users & expectations</li>
          <li>• Vision for the product</li>
          <li>• Timeline & launch goals</li>
        </ul>
      </section>

      {/* CTA */}
      <section className="mx-auto mt-32 max-w-6xl text-center">
        <h2 className="section-heading mb-6">
          Ready to build your app?
        </h2>
        <a href="/contact" className="inline-block px-8 py-3 rounded-full bg-black text-white font-medium hover:bg-opacity-80 transition">
          Contact Us
        </a>
      </section>

    </div>
  );
}
